// Staff sign-in: OAuth2 authorization-code with PKCE (S256) against d3-auth.
//
// Ported and trimmed from d3-kit's spa/auth.js, which this repo cannot import
// (d3-staff is standalone and does not vendor d3-kit). Dropped along the way:
// the Capacitor/Keychain storage adapter, the Electron login/logout openers, the
// appName key-prefixing (which mutated module-level state), and the auth event
// log. What's kept is the flow, the refresh coalescing, and the 24h rule — see
// shouldRefresh.
//
// Only staff ever touch this module. An invitee has no account and never will:
// the app shell resolves public routes before calling init(), so the invite
// surface never loads config, never reads storage, and never redirects here.

import { getBackendUrl } from './backend.js';

/**
 * The permission that grants staff access to the staff API. The API is the real
 * gate (it validates the token and checks this permission); the SPA mirrors the
 * check only so it can show a clear "not authorized" screen instead of dropping a
 * non-staff user into the app to hit a 403.
 */
export const STAFF_PERMISSION = 'access:staff';

const ACCESS_TOKEN_KEY = 'staff_access_token';
const REFRESH_TOKEN_KEY = 'staff_refresh_token';
const PKCE_VERIFIER_KEY = 'staff_pkce_verifier';

/** Refresh when the access token is within this long of expiring. */
const NEAR_EXPIRY_MS = 60_000;

/** Refresh when the access token was issued longer ago than this. */
const MAX_TOKEN_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Decode a JWT's payload without verifying it. The server verifies; the client
 * only needs `exp`/`iat` to decide when to refresh. Returns {} on anything
 * malformed rather than throwing, so a corrupt stored token degrades to "refresh
 * it" instead of breaking the app shell.
 * @param {string} token
 * @returns {{exp?: number, iat?: number, [key: string]: any}}
 */
export function decodeJwtPayload(token) {
    try {
        const payload = token.split('.')[1];
        if (!payload) return {};
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(
            normalized.length + ((4 - (normalized.length % 4)) % 4),
            '=',
        );
        return JSON.parse(atob(padded));
    } catch {
        return {};
    }
}

/**
 * Decide whether an access token needs refreshing. Pure, so the rule below is
 * actually testable.
 *
 * Refresh when either:
 *   1. the token is expired or within 60s of expiring (the usual case), or
 *   2. the token was issued more than 24h ago, regardless of its `exp`.
 *
 * Rule (2) is not redundant, and it is not a guess. It decouples the refresh
 * cadence from per-API access-token lifetime so the refresh token's idle clock
 * is reset at least daily. Without it, a service minting long-lived access
 * tokens lets the refresh token edge up against its idle TTL whenever the user
 * returns near expiry — which is exactly what logged rpgish users out after a
 * day of inactivity. Carried over from d3-kit with the bug it fixed.
 *
 * @param {{exp?: number, iat?: number}} claims
 * @param {number} now epoch millis
 * @returns {boolean}
 */
export function shouldRefresh(claims, now) {
    const expiresAt = typeof claims.exp === 'number' ? claims.exp * 1000 : 0;
    const issuedAt = typeof claims.iat === 'number' ? claims.iat * 1000 : 0;

    const nearExpiry = expiresAt !== 0 && expiresAt - now < NEAR_EXPIRY_MS;
    const tooOld = issuedAt !== 0 && now - issuedAt > MAX_TOKEN_AGE_MS;

    return nearExpiry || tooOld;
}

/**
 * Generate a PKCE verifier and its S256 challenge. d3-auth rejects
 * `code_challenge_method=plain` outright, so S256 is the only option.
 * @returns {Promise<{verifier: string, challenge: string}>}
 */
export async function generatePKCE() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const verifier = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    return { verifier, challenge };
}

/**
 * Decide whether a decoded token's claims grant staff access to the given API
 * audience. Pure, so the rule is testable. Mirrors the staff API's own check:
 * `permissions[audience]` includes `access:staff`.
 *
 * A user with no staff grant reads as false — d3-auth issues such a user a token
 * whose `aud` is the auth server itself and whose `permissions` is empty (it drops
 * the requested staff-API audience), so there is nothing to match here. The API
 * remains the real gate; this only decides which screen the SPA shows.
 *
 * @param {{permissions?: Record<string, string[]>}} claims decoded JWT payload
 * @param {string} audience the staff API identifier (d3AuthAudience from /config)
 * @returns {boolean}
 */
export function hasStaffAccess(claims, audience) {
    const granted = claims?.permissions?.[audience];
    return Array.isArray(granted) && granted.includes(STAFF_PERMISSION);
}

export const authState = {
    isAuthenticated: false,
    // isStaff is "authorized for the staff console", distinct from isAuthenticated
    // ("has a d3-auth session"). A signed-in user without access:staff is
    // authenticated but not staff — shown a not-authorized screen, not the app.
    isStaff: false,
    loading: true,
    /** @type {{subject?: string, email?: string, name?: string}|null} */
    user: null,
    /** @type {string|null} */
    error: null,
    listeners: new Set(),
    /** @type {object|null} */
    _config: null,
    /** @type {Promise<string>|null} */
    _refreshPromise: null,

    notify() {
        this.listeners.forEach((fn) => {
            fn(this);
        });
    },

    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    },

    // The OAuth redirect_uri and logout returnTo are THIS SPA's origin, not the
    // API's. That is not an oversight in the "always use getBackendUrl()"
    // convention — the browser is coming back to a page we serve, and the API
    // has no /auth/callback route. Pointing these at getBackendUrl() would break
    // sign-in in a way that only shows up against a real d3-auth.
    _redirectUri() {
        return `${window.location.origin}/auth/callback`;
    },

    /**
     * Initialize auth. Completes an in-flight OAuth callback if there is one,
     * otherwise restores an existing session.
     * @param {object} config from GET /config
     */
    async init(config) {
        this.loading = true;
        this._config = config;
        this.notify();

        try {
            const params = new URLSearchParams(window.location.search);
            const code = params.get('code');

            if (code) {
                const state = params.get('state');
                const returnPath = state ? decodeURIComponent(state) : '/';
                await this._exchangeCode(code);
                window.history.replaceState({}, document.title, returnPath);
            } else if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
                this.isAuthenticated = true;
                try {
                    const token = await this.getAccessToken();
                    await this._fetchUser(token);
                    this.isStaff = hasStaffAccess(
                        decodeJwtPayload(token),
                        this._config.d3AuthAudience,
                    );
                } catch {
                    this._clearTokens();
                    this.isAuthenticated = false;
                    this.isStaff = false;
                }
            }
        } catch (error) {
            this.error = error.message;
        }

        this.loading = false;
        this.notify();
    },

    async login() {
        if (!this._config) throw new Error('Auth not initialized');

        const { verifier, challenge } = await generatePKCE();

        // The verifier must survive a full cross-origin navigation to d3-auth
        // and back, so it cannot live in memory. That constraint is also why
        // "tokens in memory only" isn't available to us.
        localStorage.setItem(PKCE_VERIFIER_KEY, verifier);

        const query = new URLSearchParams({
            client_id: this._config.d3AuthClientID,
            redirect_uri: this._redirectUri(),
            response_type: 'code',
            audience: this._config.d3AuthAudience,
            code_challenge: challenge,
            code_challenge_method: 'S256',
            state: window.location.pathname,
        });

        // authorize is a route on the d3-auth SPA; token/userinfo/logout are on
        // the d3-auth API. Two different origins — mixing them up is the classic
        // way to break this flow.
        const tenant = this._config.d3AuthTenantSlug;
        window.location.href = `${this._config.d3AuthFrontendURL}/oauth/${tenant}/authorize?${query}`;
    },

    logout() {
        this._clearTokens();
        this.isAuthenticated = false;
        this.isStaff = false;
        this.user = null;
        this.notify();

        const query = new URLSearchParams({
            client_id: this._config.d3AuthClientID,
            returnTo: window.location.origin,
        });
        const tenant = this._config.d3AuthTenantSlug;
        window.location.href = `${this._config.d3AuthBaseURL}/oauth/${tenant}/logout?${query}`;
    },

    /**
     * Return a usable access token, refreshing first if it is near expiry or
     * stale. Callers await this rather than reading storage directly, which is
     * why the API client needs no 401-retry loop.
     * @returns {Promise<string>}
     */
    async getAccessToken() {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (!token) throw new Error('Not authenticated');

        if (shouldRefresh(decodeJwtPayload(token), Date.now())) {
            return await this._refreshTokens();
        }

        return token;
    },

    async _refreshTokens() {
        // Coalesce concurrent refreshes so N simultaneous API calls don't each
        // POST to /token. The first caller does the work; the rest await the same
        // promise and get the same new token.
        if (this._refreshPromise) return this._refreshPromise;

        this._refreshPromise = (async () => {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (!refreshToken) {
                this._signOutLocally();
                throw new Error('Not authenticated');
            }

            let response;
            try {
                response = await this._postToken({
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken,
                    client_id: this._config.d3AuthClientID,
                });
            } catch {
                this._signOutLocally();
                throw new Error('Token refresh failed');
            }

            if (!response.ok) {
                this._signOutLocally();
                throw new Error('Token refresh failed');
            }

            const data = await response.json();
            localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
            if (data.refresh_token) {
                localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
            }

            return data.access_token;
        })();

        try {
            return await this._refreshPromise;
        } finally {
            this._refreshPromise = null;
        }
    },

    async _exchangeCode(code) {
        const verifier = localStorage.getItem(PKCE_VERIFIER_KEY);
        localStorage.removeItem(PKCE_VERIFIER_KEY);
        if (!verifier) {
            throw new Error('Sign-in could not be completed — please try again');
        }

        const response = await this._postToken({
            grant_type: 'authorization_code',
            code,
            redirect_uri: this._redirectUri(),
            client_id: this._config.d3AuthClientID,
            code_verifier: verifier,
        });

        if (!response.ok) {
            throw new Error('Sign-in could not be completed — please try again');
        }

        const data = await response.json();
        localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
        if (data.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
        }

        this.isAuthenticated = true;
        this.isStaff = hasStaffAccess(
            decodeJwtPayload(data.access_token),
            this._config.d3AuthAudience,
        );
        await this._fetchUser(data.access_token);
    },

    _postToken(body) {
        const tenant = this._config.d3AuthTenantSlug;
        return fetch(`${this._config.d3AuthBaseURL}/oauth/${tenant}/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    },

    async _fetchUser(token) {
        const claims = decodeJwtPayload(token);
        this.user = { subject: claims.sub, email: claims.email, name: claims.name };
    },

    _clearTokens() {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(PKCE_VERIFIER_KEY);
    },

    _signOutLocally() {
        this._clearTokens();
        this.isAuthenticated = false;
        this.isStaff = false;
        this.user = null;
        this.notify();
    },

    // Backstop for a staff grant revoked mid-session: the cached token still
    // claims access:staff until it refreshes, so a live 403 from the staff API is
    // the first real signal. The API client calls this on a 403 so the shell
    // demotes to the not-authorized screen instead of surfacing a raw error. The
    // session itself stays intact (still signed in) — only staff authorization is
    // withdrawn.
    markNotStaff() {
        if (!this.isStaff) return;
        this.isStaff = false;
        this.notify();
    },
};

/**
 * Fetch the public client config. No bearer — this is what tells the SPA how to
 * obtain one.
 * @returns {Promise<object>}
 */
export async function fetchConfig() {
    const response = await fetch(`${getBackendUrl()}/config`);
    if (!response.ok) {
        throw new Error(`Could not load configuration: ${response.status}`);
    }
    return await response.json();
}
